"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function PhpServerSide() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    // beat : 0 = repos, 1 = la demande part, 2 = le serveur travaille,
    // 3 = la page revient, 4 = terminé (révélation).
    const [beat, setBeat] = useState(0);
    const [worked, setWorked] = useState(0); // pastilles serveur allumées (0..2)
    const timers = useRef([]);

    function clearTimers() {
        timers.current.forEach((id) => clearTimeout(id));
        timers.current = [];
    }
    useEffect(() => clearTimers, []);

    function play() {
        if (beat !== 0 && beat !== 4) return;
        clearTimers();
        setWorked(0);
        setBeat(1);
        const s = (ms) => (reduce ? ms * 0.28 : ms);
        const at = (ms, fn) => timers.current.push(setTimeout(fn, s(ms)));
        at(1000, () => setBeat(2));
        at(1350, () => setWorked(1));
        at(1900, () => setWorked(2));
        at(2600, () => setBeat(3));
        at(3600, () => {
            setBeat(4);
            setWorked(2);
        });
    }

    function replay() {
        clearTimers();
        setBeat(0);
        setWorked(0);
        // relance au tick suivant pour rejouer l'animation depuis le départ
        timers.current.push(setTimeout(play, 60));
    }

    const playing = beat >= 1 && beat <= 3;
    const stepIndex = beat <= 1 ? 0 : beat === 2 ? 1 : 2;
    const stepKeys = [
        "Php_server_step1",
        "Php_server_step2",
        "Php_server_step3",
    ];

    const packetLeft =
        beat === 1 ? "100%" : beat >= 3 ? "0%" : beat === 2 ? "100%" : "0%";
    const packetVisible = beat === 1 || beat === 3;
    const returning = beat >= 3;

    const pillLit = (i) => worked > i || beat >= 3;

    return (
        <section id="php-server-side" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Php_server_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Php_server_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:php"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        mon-site.fr
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Php_server_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    {/* Scène : Vous --- (paquet) --- Le serveur */}
                    <div className="flex items-center justify-between gap-3 sm:gap-5">
                        {/* Vous */}
                        <motion.div
                            animate={
                                beat === 1 && !reduce
                                    ? { scale: [1, 1.06, 1] }
                                    : { scale: 1 }
                            }
                            transition={{ duration: 0.6 }}
                            className="shrink-0 w-24 sm:w-32 flex flex-col items-center gap-2"
                        >
                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/[0.05] ring-1 ring-white/10 flex items-center justify-center">
                                <SolarIcon
                                    name="smartphone-bold"
                                    className="w-7 h-7 text-gray-200"
                                />
                            </div>
                            <span className="text-xs font-semibold text-gray-300">
                                {t("Php_server_you")}
                            </span>
                        </motion.div>

                        {/* Piste + paquet */}
                        <div className="relative flex-1 h-12">
                            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-white/5 via-white/20 to-white/5" />
                            <motion.div
                                className="absolute top-1/2"
                                initial={{ left: "0%", opacity: 0 }}
                                animate={{
                                    left: packetLeft,
                                    opacity: packetVisible ? 1 : 0,
                                }}
                                transition={{
                                    left: {
                                        duration: reduce ? 0 : 0.9,
                                        ease: "easeInOut",
                                    },
                                    opacity: { duration: 0.25 },
                                }}
                                style={{ transform: "translate(-50%, -50%)" }}
                            >
                                <span
                                    className={`flex items-center justify-center w-6 h-6 rounded-full ${
                                        returning
                                            ? "bg-white text-indigo-600"
                                            : "bg-cyan-300 text-cyan-900"
                                    }`}
                                    style={{
                                        boxShadow: returning
                                            ? "0 0 18px 5px rgba(165,180,252,0.6)"
                                            : "0 0 18px 5px rgba(103,232,249,0.55)",
                                    }}
                                >
                                    <SolarIcon
                                        name={
                                            returning
                                                ? "document-text-bold"
                                                : "cursor-bold"
                                        }
                                        className="w-3.5 h-3.5"
                                    />
                                </span>
                            </motion.div>
                        </div>

                        {/* Le serveur */}
                        <div className="shrink-0 w-36 sm:w-48 relative">
                            <motion.div
                                animate={
                                    beat === 2 && !reduce
                                        ? { opacity: [0.3, 0.7, 0.3] }
                                        : { opacity: beat === 2 ? 0.5 : 0 }
                                }
                                transition={{
                                    duration: 1.2,
                                    repeat: beat === 2 ? Infinity : 0,
                                }}
                                className="absolute -inset-3 rounded-3xl bg-indigo-500/25 blur-xl pointer-events-none"
                            />
                            <div className="relative rounded-2xl bg-white/[0.04] ring-1 ring-indigo-400/20 p-3.5">
                                <div className="flex items-center gap-2 mb-3">
                                    <SolarIcon
                                        name="server-square-bold"
                                        className="w-5 h-5 text-indigo-300"
                                    />
                                    <span className="text-sm font-semibold text-white">
                                        {t("Php_server_server")}
                                    </span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {[
                                        {
                                            icon: "code-square-bold",
                                            label: "Php_server_chip_code",
                                        },
                                        {
                                            icon: "database-bold",
                                            label: "Php_server_chip_db",
                                        },
                                    ].map((pill, i) => {
                                        const lit = pillLit(i);
                                        return (
                                            <div
                                                key={pill.label}
                                                className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 ring-1 transition-colors duration-300 ${
                                                    lit
                                                        ? "bg-indigo-500/15 ring-indigo-400/40"
                                                        : "bg-white/[0.03] ring-white/10"
                                                }`}
                                            >
                                                <SolarIcon
                                                    name={pill.icon}
                                                    className={`w-4 h-4 shrink-0 ${
                                                        lit
                                                            ? "text-indigo-300"
                                                            : "text-gray-500"
                                                    }`}
                                                />
                                                <span
                                                    className={`text-[12px] font-medium truncate ${
                                                        lit
                                                            ? "text-white"
                                                            : "text-gray-500"
                                                    }`}
                                                >
                                                    {t(pill.label)}
                                                </span>
                                                {beat >= 4 ? (
                                                    <SolarIcon
                                                        name="lock-keyhole-bold"
                                                        className="w-3.5 h-3.5 text-indigo-300 ml-auto shrink-0"
                                                    />
                                                ) : (
                                                    lit && (
                                                        <SolarIcon
                                                            name="check-circle-bold"
                                                            className="w-3.5 h-3.5 text-emerald-400 ml-auto shrink-0"
                                                        />
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Statut + étapes */}
                    <div className="mt-7 flex flex-col items-center text-center">
                        <div className="flex items-center gap-2 mb-3">
                            {[0, 1, 2].map((i) => (
                                <span
                                    key={i}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        beat >= 1 && i <= stepIndex
                                            ? "w-7 bg-indigo-400"
                                            : "w-2.5 bg-white/15"
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="h-6">
                            <AnimatePresence mode="wait">
                                {beat >= 1 && (
                                    <motion.p
                                        key={stepIndex}
                                        initial={
                                            reduce ? false : { opacity: 0, y: 6 }
                                        }
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        transition={{ duration: 0.25 }}
                                        className="text-sm font-medium text-gray-200 mb-0"
                                    >
                                        {t(stepKeys[stepIndex])}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </div>

                        {beat === 0 && (
                            <button
                                type="button"
                                onClick={play}
                                className="mt-1 inline-flex items-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition active:scale-95"
                            >
                                <SolarIcon name="play-bold" className="w-4 h-4" />
                                {t("Php_server_cta")}
                            </button>
                        )}
                        {beat === 4 && (
                            <button
                                type="button"
                                onClick={replay}
                                className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3.5 py-1.5 text-[13px] text-gray-300 transition active:scale-95"
                            >
                                <SolarIcon
                                    name="restart-bold-duotone"
                                    className="w-3.5 h-3.5"
                                />
                                {t("Php_server_replay")}
                            </button>
                        )}
                        {playing && (
                            <span className="mt-1 inline-flex items-center gap-1.5 text-[13px] text-gray-500">
                                <SolarIcon
                                    name="refresh-bold"
                                    className={`w-3.5 h-3.5 ${reduce ? "" : "animate-spin"}`}
                                />
                                {t("Php_server_loading")}
                            </span>
                        )}
                    </div>

                    {/* Résultat : la page reçue + la révélation */}
                    <AnimatePresence>
                        {beat >= 3 && (
                            <motion.div
                                initial={
                                    reduce ? false : { opacity: 0, y: 14 }
                                }
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                className="mt-8 grid md:grid-cols-2 gap-5 items-center"
                            >
                                {/* La page reçue */}
                                <div className="flex flex-col items-center">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 mb-2">
                                        {t("Php_server_received")}
                                    </span>
                                    <motion.div
                                        initial={
                                            reduce
                                                ? false
                                                : { scale: 0.94, opacity: 0 }
                                        }
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 220,
                                            damping: 20,
                                        }}
                                        className="w-full max-w-[300px] rounded-xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/10 overflow-hidden"
                                    >
                                        <div className="h-11 bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center gap-2.5 px-4">
                                            <span className="w-7 h-7 rounded-full bg-white/25 flex items-center justify-center">
                                                <SolarIcon
                                                    name="user-rounded-bold"
                                                    className="w-4 h-4 text-white"
                                                />
                                            </span>
                                            <span className="text-sm font-bold text-white">
                                                {t("Php_server_page_hello")}
                                            </span>
                                        </div>
                                        <div className="p-4">
                                            <p className="text-sm text-slate-500 mb-3">
                                                {t("Php_server_page_sub")}
                                            </p>
                                            <span className="inline-flex items-center rounded-lg bg-indigo-500 px-3.5 py-2 text-sm font-semibold text-white">
                                                {t("Php_server_page_button")}
                                            </span>
                                        </div>
                                    </motion.div>
                                </div>

                                {/* La révélation */}
                                <AnimatePresence>
                                    {beat >= 4 && (
                                        <motion.div
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0, x: 12 }
                                            }
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4 }}
                                            className="flex items-start gap-3 rounded-2xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-5"
                                        >
                                            <SolarIcon
                                                name="lightbulb-bolt-bold-duotone"
                                                className="w-6 h-6 text-indigo-300 shrink-0 mt-0.5"
                                            />
                                            <p className="text-sm text-gray-200 leading-relaxed mb-0">
                                                {t("Php_server_reveal")}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Php_server_caption")}
            </p>
        </section>
    );
}
